/**
 * Codex rollout JSONL → Claude-shaped conversation lines for WS clients.
 *
 * Mobile's `parseLineToMessage` only understands Claude Code JSONL
 * (`type: "user"|"assistant"`, `message.role`, text blocks). Codex writes
 * `response_item` / `event_msg` / `session_meta` instead, so raw Codex lines
 * never become live bubbles. This helper normalizes chat-bearing Codex lines
 * into the Claude shape and drops everything else (including the duplicate
 * `event_msg` copies of each turn and `developer` role payloads).
 */

import {
  isCodexInjectedContext as isCodexFormatPreamble,
  parseCodexJsonlLine,
} from "@threadbase-sh/scanner";
import type { NormalizeResult } from "../services/providers/capabilities";
import { toClaudeShapedLine } from "./claudeShapedLine";
import { classifyCursorLine, isCursorTranscriptLine } from "./cursorConversationLine";

type CodexContentBlock = {
  type?: string;
  text?: string;
};

function extractCodexText(content: unknown): string {
  if (typeof content === "string") return content.trim();
  if (!Array.isArray(content)) return "";
  return content
    .map((item) => {
      if (typeof item === "string") return item;
      const block = item as CodexContentBlock;
      const t = block?.type;
      if (
        (t === "input_text" || t === "output_text" || t === "text") &&
        typeof block.text === "string"
      ) {
        return block.text;
      }
      return "";
    })
    .filter(Boolean)
    .join("")
    .trim();
}

// Rollout envelope types this adapter understands. Anything outside this set is
// reported as `unknown` rather than dropped — a Codex release that renames or
// adds an envelope must be visible, not silently render an empty conversation.
// See docs/architecture/2026-07-24-provider-compatibility.md.
const KNOWN_CODEX_TYPES = new Set(["response_item", "event_msg", "session_meta", "turn_context"]);

/**
 * Classify one Codex rollout line (C2).
 *
 * Distinguishes "recognized, deliberately not rendered" (`ignored`) from "this
 * adapter has never seen this shape" (`unknown`). Both were `null` before, which
 * is why provider schema drift used to surface as an empty screen with no error.
 *
 * `index` is the line's message_index in its FILE, which is what the leading-turn
 * bound needs. The default of 0 treats an unknown position as leading, so a
 * caller that cannot say keeps the injected-context filter rather than
 * rendering a fake AGENTS.md bubble.
 */
export function classifyCodexLine(line: string, index = 0): NormalizeResult {
  let entry: {
    type?: string;
    timestamp?: string;
    payload?: {
      type?: string;
      role?: string;
      content?: unknown;
      id?: string;
    };
  };
  try {
    entry = JSON.parse(line);
  } catch {
    return { kind: "unknown", raw: line, reason: "line is not valid JSON" };
  }

  if (typeof entry.type !== "string" || !KNOWN_CODEX_TYPES.has(entry.type)) {
    return {
      kind: "unknown",
      raw: line,
      reason: `unrecognized rollout envelope type: ${String(entry.type)}`,
    };
  }

  // Recognized envelopes that legitimately carry no renderable chat.
  // event_msg duplicates each turn; session_meta/turn_context are headers.
  if (entry.type !== "response_item") {
    return { kind: "ignored", reason: `${entry.type} carries no chat content` };
  }

  const payload = entry.payload;
  if (payload?.type && CODEX_BLOCK_PAYLOADS.has(payload.type)) {
    return classifyCodexBlockItem(line, entry, payload.type);
  }
  if (payload?.type !== "message") {
    return { kind: "ignored", reason: `response_item payload is ${String(payload?.type)}` };
  }

  const role = payload.role;
  if (role !== "user" && role !== "assistant") {
    // `developer` and friends are known Codex roles we deliberately hide.
    return { kind: "ignored", reason: `role ${String(role)} is not rendered` };
  }

  const text = extractCodexText(payload.content);
  if (!text) {
    return { kind: "ignored", reason: "message has no extractable text" };
  }

  // Synthetic Codex context dumps (AGENTS.md / permissions instructions) are
  // written as `role: user` before any real user turn. Hide them from the live
  // overlay so the chat doesn't open with fake user bubbles — but only at the
  // head, the same bound REST applies, or a user's pasted text vanishes live.
  if (isLeadingInjectedContext(index, role, text)) {
    return { kind: "ignored", reason: "synthetic injected context" };
  }

  return { kind: "message", line: buildClaudeShapedLine(entry, payload, role, text) };
}

// Tool calls, their outputs and reasoning. The scanner owns how these render
// (the same parse serves them over REST), so the live line is built from its
// result rather than a second reading of the rollout format.
const CODEX_BLOCK_PAYLOADS = new Set([
  "function_call",
  "custom_tool_call",
  "web_search_call",
  "function_call_output",
  "custom_tool_call_output",
  "reasoning",
]);

function classifyCodexBlockItem(
  line: string,
  entry: { timestamp?: string; payload?: { id?: string; call_id?: string } },
  payloadType: string,
): NormalizeResult {
  const message = parseCodexJsonlLine(line);
  if (!message) {
    return { kind: "ignored", reason: `${payloadType} has nothing renderable` };
  }
  const uuid =
    message.uuid ??
    `codex-${payloadType}-${entry.timestamp ?? ""}-${entry.payload?.call_id ?? hashPrefix(line)}`;
  return { kind: "message", line: toClaudeShapedLine(message, uuid) };
}

/**
 * Returns a Claude-shaped JSONL line, or null when the input is not a
 * user/assistant chat message that clients should render.
 *
 * Thin wrapper over classifyCodexLine so existing callers keep their signature.
 * New code should prefer classifyCodexLine, which explains WHY a line produced
 * nothing.
 */
export function normalizeCodexLineToClaudeShape(line: string, index = 0): string | null {
  const result = classifyCodexLine(line, index);
  return result.kind === "message" ? result.line : null;
}

function buildClaudeShapedLine(
  entry: { timestamp?: string },
  payload: { id?: string },
  role: "user" | "assistant",
  text: string,
): string {
  const timestamp =
    typeof entry.timestamp === "string" ? entry.timestamp : new Date().toISOString();
  // Prefer payload.id when present; otherwise derive a stable-enough id from
  // timestamp+role+text prefix so WS seenIds can dedupe without colliding
  // across consecutive turns in the same second.
  const uuid =
    typeof payload.id === "string" && payload.id.length > 0
      ? payload.id
      : `codex-${role}-${timestamp}-${hashPrefix(text)}`;

  return JSON.stringify({
    type: role,
    uuid,
    timestamp,
    message: {
      role,
      content: [{ type: "text", text }],
    },
  });
}

/** True when a Codex rollout line is Codex-shaped (not Claude JSONL). */
export function isCodexRolloutLine(line: string): boolean {
  try {
    const entry = JSON.parse(line) as { type?: string };
    return (
      entry.type === "response_item" ||
      entry.type === "event_msg" ||
      entry.type === "session_meta" ||
      entry.type === "turn_context"
    );
  } catch {
    return false;
  }
}

/**
 * Map a batch of raw JSONL lines to client-facing lines. Codex and Cursor
 * batches are normalized (and filtered); Claude batches pass through unchanged.
 *
 * `seqs` are the offset index's message_index per line (extendMessageIndex),
 * and they are the only position this batch can be trusted with: a batch is one
 * watcher read, which may start anywhere in the file, so "first in batch" is not
 * "first in conversation". The index numbers from its persisted
 * last_message_index and declines a read that does not start where it left off,
 * so a seq is a file position, never a batch-relative guess. A line with no seq
 * (the index dropped it, cannot index this file, or declined the read) falls
 * back to leading, which keeps the injected-context filter.
 *
 * The returned `seqs` stay parallel to the returned `lines` — filtered with
 * them — or are null when the caller passed none that line up with `lines`.
 */
export function toClientConversationLines(
  lines: string[],
  seqs?: (number | null)[] | null,
): { lines: string[]; seqs: (number | null)[] | null } {
  const positions = seqs?.length === lines.length ? seqs : null;
  // Heuristic: the first recognisable line decides the batch's provider (a
  // mixed batch shouldn't happen — one file, one provider).
  const classify = lines.some(isCodexRolloutLine)
    ? (line: string, i: number) => classifyCodexLine(line, positions?.[i] ?? 0)
    : lines.some(isCursorTranscriptLine)
      ? (line: string, i: number) => classifyCursorLine(line, positions?.[i])
      : null;
  if (!classify) return { lines, seqs: positions };
  const out: string[] = [];
  const outSeqs: (number | null)[] = [];
  for (const [i, line] of lines.entries()) {
    const result = classify(line, i);
    if (result.kind !== "message") continue;
    out.push(result.line);
    outSeqs.push(positions?.[i] ?? null);
  }
  return { lines: out, seqs: positions ? outSeqs : null };
}

/**
 * Prompts THIS server injects, passed as Codex CLI argv because there is no
 * --system-prompt flag. DEFAULT_SYSTEM_PROMPT + BROWSE_SYSTEM_PROMPT land in the
 * rollout as `role: user`.
 *
 * Deliberately NOT in the scanner: recognising our own output is not Codex
 * format knowledge, and a general rollout reader has no business knowing what
 * this server says.
 */
function isStreamerInjectedPrompt(text: string): boolean {
  if (text.includes("limit the options to at most 3")) return true;
  if (text.includes("You are working within the project boundary:")) return true;
  return text.includes(
    "Do not read, write, or execute commands that access files or directories outside this boundary",
  );
}

/**
 * Text that is machinery rather than a turn the user typed.
 *
 * Two concerns, one question. The Codex-format preambles (AGENTS.md dumps,
 * sandbox blobs) come from the scanner, which owns the rollout format and
 * applies the same predicate when it counts messages — importing it is what
 * keeps its index space and ours identical. The argv prompts above are ours.
 *
 * Callers MUST bound this to the leading turn (see isLeadingInjectedContext).
 */
export function isCodexInjectedContext(text: string): boolean {
  return isCodexFormatPreamble(text) || isStreamerInjectedPrompt(text);
}

/**
 * The form every caller should actually use.
 *
 * Codex only injects at the head of a rollout, so the skip is bounded to the
 * leading turn — otherwise a human who pastes instruction text mid-conversation
 * has their message silently eaten, and message_count stops matching what they
 * can see. The scanner bounds it the same way (`acc.messageCount === 0`), and
 * the two must agree or the index counts in a different space than we serve.
 */
export function isLeadingInjectedContext(
  index: number,
  role: string,
  text: string | undefined,
): boolean {
  return index === 0 && role === "user" && typeof text === "string" && isCodexInjectedContext(text);
}

function hashPrefix(text: string): string {
  // Short non-crypto fingerprint for id uniqueness within a session.
  let h = 0;
  const sample = text.slice(0, 64);
  for (let i = 0; i < sample.length; i++) {
    h = (h * 31 + sample.charCodeAt(i)) | 0;
  }
  return Math.abs(h).toString(36);
}
